import { lazy, Suspense } from 'react'
import type { RouteObject } from 'react-router'
import { Navigate } from 'react-router'
import Layout from './components/layout/logged/Layout'
import MainLayout from './components/layout/logout/MainLayout'
import LoadingScreen from './components/LoadingScreen'
import Activity from './pages/gameRelated/Activity'
import LegacyGroupsRedirect from './pages/gameRelated/Locations/legacy-groups-redirect'
import LegacyLocationsRedirect from './pages/gameRelated/Locations/legacy-locations-redirect'
import AdminManagementGuard from './services/auth/guards/AdminManagementGuard'
import AuthGuard from './services/auth/guards/AuthGuard'
import GlobalModulesAccessGuard from './services/auth/guards/GlobalModulesAccessGuard'
import GuestGuard from './services/auth/guards/GuestGuard'
import { PermissionProvider } from './services/auth/permissions/PermissionProvider'

const Loadable = (Component) => (props) => (
  <Suspense fallback={<LoadingScreen />}>
    <Component {...props} />
  </Suspense>
)

const Dashboard = Loadable(lazy(() => import('./pages/Dashboard')))

const PlayersSearch = Loadable(lazy(() => import('./pages/players')))

const Games = Loadable(lazy(() => import('./pages/gameRelated/List')))
const GameLayout = Loadable(lazy(() => import('./components/layout/game')))
const Stats = Loadable(lazy(() => import('./pages/gameRelated/Stats')))
const GameEdit = Loadable(lazy(() => import('./pages/gameRelated/Settings')))
const Players = Loadable(lazy(() => import('./pages/gameRelated/Players')))
const PlayerDetails = Loadable(
  lazy(() => import('./pages/gameRelated/Players/components/PlayerDetails'))
)
const LeaderboardsTypes = Loadable(
  lazy(() => import('./pages/gameRelated/Leaderboards'))
)
const LeaderboardTypesEdit = Loadable(
  lazy(() => import('./pages/gameRelated/Leaderboards/components/Edit'))
)
const Resources = Loadable(lazy(() => import('./pages/gameRelated/Resources')))
const Cards = Loadable(lazy(() => import('./pages/gameRelated/Cards')))
const CardsEdit = Loadable(
  lazy(() => import('./pages/gameRelated/Cards/TeamRanges/components/EditTag'))
)
const TeamRangesCreate = Loadable(
  lazy(() => import('./pages/gameRelated/Cards/TeamRanges/components/Create'))
)
const Demographics = Loadable(
  lazy(() => import('./pages/gameRelated/Demographics'))
)
const TeamsAnalytics = Loadable(
  lazy(() => import('./pages/gameRelated/TeamsAnalytics'))
)
const MappingAnalytics = Loadable(
  lazy(() => import('./pages/gameRelated/MappingAnalytics'))
)
const MappingV2 = Loadable(lazy(() => import('./pages/mapping-v2')))
const Surveys = Loadable(lazy(() => import('./pages/gameRelated/Surveys')))

const TeamsList = Loadable(lazy(() => import('./pages/gameRelated/Teams')))
const TeamDetails = Loadable(
  lazy(() => import('./pages/gameRelated/Teams/components/TeamDetails'))
)

const MapWorkspace = Loadable(
  lazy(() => import('./pages/gameRelated/Locations'))
)
const MapStatus = Loadable(
  lazy(() => import('./pages/gameRelated/Locations/workflow'))
)
const MapSettings = Loadable(
  lazy(() => import('./pages/gameRelated/Locations/settings'))
)
const MapReviewers = Loadable(
  lazy(() => import('./pages/gameRelated/Locations/reviewers/reviewers-page'))
)
const Installer = Loadable(lazy(() => import('./pages/gameRelated/Installer')))
const TagRangeEdit = Loadable(
  lazy(() => import('./pages/gameRelated/Cards/GameRanges/components/EditTags'))
)
const CardOrders = Loadable(lazy(() => import('./pages/cardOrders')))
const DataLibrary = Loadable(lazy(() => import('./pages/data-library')))
const CardOrderEdit = Loadable(
  lazy(() => import('./pages/cardOrders/components/Edit'))
)
const EventsScheduleDetails = Loadable(
  lazy(() => import('./pages/gameRelated/Events/components/Details'))
)
const EventsScheduleCreate = Loadable(
  lazy(() => import('./pages/gameRelated/Events/components/Create'))
)
const EventSchedule = Loadable(lazy(() => import('./pages/gameRelated/Events')))
const Login = Loadable(lazy(() => import('./pages/auth/Login')))
const ReviewerReview = Loadable(lazy(() => import('./pages/reviewer-review')))

const Messages = Loadable(
  lazy(() => import('./pages/gameRelated/Engagement/Messages'))
)
const CreateMessage = Loadable(
  lazy(
    () => import('./pages/gameRelated/Engagement/Messages/components/Create')
  )
)
const TeamMessages = Loadable(
  lazy(() => import('./pages/gameRelated/Engagement/team-messages'))
)

const LuckyTapList = Loadable(
  lazy(() => import('./pages/gameRelated/prizes/lucky-taps/list'))
)
const LuckyTapSettings = Loadable(
  lazy(() => import('./pages/gameRelated/prizes/lucky-taps/settings'))
)
const PrizeDraws = Loadable(
  lazy(() => import('./pages/gameRelated/prizes/prize-draws'))
)
const PrizeDrawsCreate = Loadable(
  lazy(() => import('./pages/gameRelated/prizes/prize-draws/create'))
)
const PrizeDrawDetails = Loadable(
  lazy(() => import('./pages/gameRelated/prizes/prize-draws/details'))
)
const AdminsPage = Loadable(lazy(() => import('./pages/admins')))
const AdminDetailsPage = Loadable(lazy(() => import('./pages/admins/details')))
const CreateAdminPage = Loadable(lazy(() => import('./pages/admins/create')))
const AdminRolesPage = Loadable(lazy(() => import('./pages/admins/roles')))
const AdminProfilePage = Loadable(lazy(() => import('./pages/admins/profile')))

const AuthorizationRequired = Loadable(
  lazy(() => import('./pages/error/AuthorizationRequired'))
)
const NotFound = Loadable(lazy(() => import('./pages/error/NotFound')))
const ServerError = Loadable(lazy(() => import('./pages/error/ServerError')))

const messageCentreRoutes = [
  {
    path: '/games/:slug/engagement/messages',
    element: <Messages />,
  },
  {
    path: '/games/:slug/engagement/team-messages',
    element: <TeamMessages />,
  },
  {
    path: '/games/:slug/engagement/messages/create',
    element: (
      <PermissionProvider permission='message:write' showAccessDenied>
        <CreateMessage />
      </PermissionProvider>
    ),
  },
]

const routes: RouteObject[] = [
  {
    path: '',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        path: '/',
        element: <Dashboard />,
      },
      {
        path: '/players',
        element: (
          <GlobalModulesAccessGuard>
            <PlayersSearch />
          </GlobalModulesAccessGuard>
        ),
      },
      {
        path: '/players/details/:playerId',
        element: (
          <GlobalModulesAccessGuard>
            <PlayerDetails />
          </GlobalModulesAccessGuard>
        ),
      },
      {
        path: '/games',
        element: <Games />,
      },
      {
        path: '/games/:slug',
        element: <GameLayout />,
        children: [
          {
            index: true,
            element: <Navigate to='overview' replace />,
          },
          {
            path: '/games/:slug/overview',
            element: <Stats />,
          },
          {
            path: '/games/:slug/general',
            element: (
              <PermissionProvider permission='game:update' showAccessDenied>
                <GameEdit />
              </PermissionProvider>
            ),
          },
          {
            path: '/games/:slug/surveys',
            element: <Surveys />,
          },
          {
            path: '/games/:slug/players',
            element: <Players />,
          },
          {
            path: '/games/:slug/players/details/:playerId',
            element: <PlayerDetails />,
          },
          {
            path: '/games/:slug/leaderboards',
            element: <LeaderboardsTypes />,
          },
          {
            path: '/games/:slug/leaderboards/edit/:leaderboardId',
            element: <LeaderboardTypesEdit />,
          },
          {
            path: '/games/:slug/resources',
            element: <Resources />,
          },
          {
            path: '/games/:slug/teams',
            element: <TeamsList />,
          },
          {
            path: '/games/:slug/teams/details/:teamId',
            element: <TeamDetails />,
          },
          {
            path: '/games/:slug/cards/game-ranges',
            element: <Cards />,
          },
          {
            path: '/games/:slug/cards/team-ranges',
            element: <Cards />,
          },
          {
            path: '/games/:slug/cards/team-ranges/create',
            element: (
              <PermissionProvider permission='card:write' showAccessDenied>
                <TeamRangesCreate />
              </PermissionProvider>
            ),
          },
          {
            path: '/games/:slug/cards/edit/:cardsId',
            element: <CardsEdit />,
          },
          {
            path: '/games/:slug/events',
            element: <EventSchedule />,
          },
          {
            path: '/games/:slug/events/create',
            element: (
              <PermissionProvider permission='event:write' showAccessDenied>
                <EventsScheduleCreate />
              </PermissionProvider>
            ),
          },
          {
            path: '/games/:slug/events/details/:eventId',
            element: (
              <PermissionProvider permission='event:write' showAccessDenied>
                <EventsScheduleDetails />
              </PermissionProvider>
            ),
          },
          {
            path: '/games/:slug/map',
            element: <Navigate to='locations' replace />,
          },
          {
            path: '/games/:slug/map/status',
            element: (
              <PermissionProvider permission='location:create' showAccessDenied>
                <MapStatus />
              </PermissionProvider>
            ),
          },
          {
            path: '/games/:slug/map/inbox',
            element: <Navigate to='../locations?inbox=open' replace />,
          },
          {
            path: '/games/:slug/map/tasks',
            element: <Navigate to='../locations?inbox=open' replace />,
          },
          {
            path: '/games/:slug/map/status/reviewers',
            element: (
              <PermissionProvider permission='location:create' showAccessDenied>
                <MapReviewers />
              </PermissionProvider>
            ),
          },
          {
            path: '/games/:slug/map/installer',
            element: (
              <PermissionProvider
                permission='location:install'
                showAccessDenied
              >
                <Installer />
              </PermissionProvider>
            ),
          },
          {
            path: '/games/:slug/map/groups',
            element: <MapWorkspace section='groups' />,
          },
          {
            path: '/games/:slug/map/datasets',
            element: <MapWorkspace section='datasets' />,
          },
          {
            path: '/games/:slug/map/settings',
            element: <MapSettings />,
          },
          {
            path: '/games/:slug/map/locations/groups',
            element: <LegacyGroupsRedirect />,
          },
          {
            path: '/games/:slug/map/locations',
            element: <MapWorkspace />,
          },
          {
            path: '/games/:slug/locations',
            element: <LegacyLocationsRedirect />,
          },
          {
            path: '/games/:slug/installer',
            element: <Navigate to='../map/installer' replace />,
          },
          {
            path: '/games/:slug/location',
            element: <Navigate to='../map/locations' replace />,
          },
          {
            path: '/games/:slug/mapping',
            element: <Navigate to='../map/locations' replace />,
          },
          {
            path: '/games/:slug/tagRange/edit/:tagId',
            element: <TagRangeEdit />,
          },
          {
            path: '/games/:slug/demographics',
            element: <Demographics />,
          },
          {
            path: '/games/:slug/analytics-teams',
            element: <TeamsAnalytics />,
          },
          {
            path: '/games/:slug/activity',
            element: <Activity />,
          },
          {
            path: '/games/:slug/mapping-analytics',
            element: <MappingAnalytics />,
          },
          {
            path: '/games/:slug/mapping-v2',
            element: <MappingV2 />,
          },
          ...messageCentreRoutes,
          {
            path: '/games/:slug/prizes/prize-draws',
            element: <PrizeDraws />,
          },
          {
            path: '/games/:slug/prizes/prize-draws/create',
            element: (
              <PermissionProvider permission='prize:write' showAccessDenied>
                <PrizeDrawsCreate />
              </PermissionProvider>
            ),
          },
          {
            path: '/games/:slug/prizes/prize-draws/:drawId',
            element: <PrizeDrawDetails />,
          },
          {
            path: '/games/:slug/prizes/lucky-taps',
            element: <LuckyTapList />,
          },
          {
            path: '/games/:slug/prizes/lucky-taps/settings',
            element: <LuckyTapSettings />,
          },
        ],
      },
      {
        path: '/admin/admins',
        element: (
          <AdminManagementGuard>
            <AdminsPage />
          </AdminManagementGuard>
        ),
      },
      {
        path: '/admin/admins/create',
        element: (
          <AdminManagementGuard>
            <CreateAdminPage />
          </AdminManagementGuard>
        ),
      },
      {
        path: '/admin/admins/:adminId',
        element: (
          <AdminManagementGuard>
            <AdminDetailsPage />
          </AdminManagementGuard>
        ),
      },
      {
        path: '/admin/roles',
        element: (
          <AdminManagementGuard>
            <AdminRolesPage />
          </AdminManagementGuard>
        ),
      },
      {
        path: '/admin/profile',
        element: <AdminProfilePage />,
      },
      {
        path: '/card-orders',
        element: (
          <GlobalModulesAccessGuard>
            <CardOrders />
          </GlobalModulesAccessGuard>
        ),
      },
      {
        path: '/data-library',
        element: (
          <GlobalModulesAccessGuard>
            <DataLibrary />
          </GlobalModulesAccessGuard>
        ),
      },
      {
        path: '/card-orders/edit/:cardOrderId',
        element: (
          <GlobalModulesAccessGuard>
            <CardOrderEdit />
          </GlobalModulesAccessGuard>
        ),
      },
      {
        path: '*',
        element: <Dashboard />,
      },
    ],
  },
  {
    path: 'login',
    element: (
      <GuestGuard>
        <Login />
      </GuestGuard>
    ),
  },
  {
    path: '/games/:slug/review',
    element: <ReviewerReview />,
  },
  {
    path: '*',
    element: <MainLayout />,
    children: [
      {
        path: '401',
        element: <AuthorizationRequired />,
      },
      {
        path: '404',
        element: <NotFound />,
      },
      {
        path: '500',
        element: <ServerError />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
]

export default routes
